export const INR_FORMATTER = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

export const NUMBER_FORMATTER = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
});

export const getTodayIST = () => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());

  const year = parts.find((p) => p.type === "year")?.value;
  const month = parts.find((p) => p.type === "month")?.value;
  const day = parts.find((p) => p.type === "day")?.value;

  return `${year}-${month}-${day}`;
};

export const addDays = (dateString, days) => {
  const date = new Date(`${dateString}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);

  return date.toISOString().slice(0, 10);
};

export const getDefaultDateRange = () => {
  const to = getTodayIST();

  return {
    from: addDays(to, -29),
    to,
  };
};

export const getFinancialYearRange = () => {
  const today = getTodayIST();
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));

  const startYear = month >= 4 ? year : year - 1;

  return {
    from: `${startYear}-04-01`,
    to: `${startYear + 1}-03-31`,
  };
};

export const getDatePresetRange = (preset) => {
  const today = getTodayIST();

  switch (preset) {
    case "today":
      return {
        from: today,
        to: today,
      };

    case "7d":
      return {
        from: addDays(today, -6),
        to: today,
      };

    case "30d":
      return {
        from: addDays(today, -29),
        to: today,
      };

    case "thisMonth": {
      const year = today.slice(0, 4);
      const month = today.slice(5, 7);

      return {
        from: `${year}-${month}-01`,
        to: today,
      };
    }

    case "lastMonth": {
      const date = new Date(`${today}T00:00:00Z`);
      date.setUTCDate(1);
      date.setUTCMonth(date.getUTCMonth() - 1);

      const year = date.getUTCFullYear();
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");

      const lastDay = new Date(
        Date.UTC(year, date.getUTCMonth() + 1, 0),
      ).getUTCDate();

      return {
        from: `${year}-${month}-01`,
        to: `${year}-${month}-${String(lastDay).padStart(2, "0")}`,
      };
    }

    case "fy":
      return getFinancialYearRange();

    default:
      return getDefaultDateRange();
  }
};

export const formatINR = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return INR_FORMATTER.format(Number(value));
};

export const formatNumber = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return NUMBER_FORMATTER.format(Number(value));
};

export const formatPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "—";
  }

  return `${Number(value).toFixed(2)}%`;
};

export const formatDateIST = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(`${value}T00:00:00+05:30`);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

export const formatDateTimeIST = (value) => {
  if (!value) {
    return "—";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const toNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

export const sumBy = (rows, key) => {
  return rows.reduce((total, row) => total + toNumber(row[key]), 0);
};

export const averageBy = (rows, key) => {
  if (!rows.length) {
    return 0;
  }

  return (
    rows.reduce((total, row) => total + toNumber(row[key]), 0) / rows.length
  );
};

export const getValue = (row, key) => {
  const value = row?.[key];

  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return value;
};

export const safeText = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  return String(value).toLowerCase();
};